import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import AskDex from "@/pages/AskDex";
import ExecOverview from "@/pages/ExecOverview";
import Anomalies from "@/pages/Anomalies";
import CloudEconomics from "@/pages/CloudEconomics";
import ContainerOptimization from "@/pages/ContainerOptimization";
import UnitEconomics from "@/pages/UnitEconomics";
import TokenMind from "@/pages/TokenMind";
import Governance from "@/pages/Governance";
import Opportunities from "@/pages/Opportunities";
import Remediations from "@/pages/Remediations";
import Settings from "@/pages/Settings";
import LLMConfig from "@/pages/LLMConfig";
import SavingsHub from "@/pages/SavingsHub";
import ReportCenter from "@/pages/ReportCenter";
import PartnerOps from "@/pages/PartnerOps";
import InvoicePrint from "@/pages/InvoicePrint";
import PortalLogin from "@/pages/PortalLogin";
import Portal from "@/pages/Portal";
import PortalInvoicePrint from "@/pages/PortalInvoicePrint";

const TITLES = {
  "/dex": {
    title: "Ask Dex",
    desc: "Your FinOps assistant — grounded in the latest scan",
  },
  "/executive": {
    title: "CFO Overview",
    desc: "Executive summary, budget variance, forecast, savings funnel & AI insight",
  },
  "/containers": {
    title: "Container Optimization",
    desc: "ASTRA KubeEconomics — fleet coverage, container right-sizing, bin-packing, cross-zone network & realized savings",
  },
  "/anomalies": {
    title: "Anomaly Intelligence",
    desc: "Detected spend anomalies with baseline deviation, confidence & business dimensions",
  },
  "/cloud-economics": {
    title: "Cloud Economics",
    desc: "Multi-cloud spend, application cost allocation, business-unit ownership & forecast",
  },
  "/unit-economics": {
    title: "Unit Economics",
    desc: "Cost per customer, product, feature and unit — with margin & profitability analysis",
  },
  "/tokenmind": {
    title: "ASTRA TokenMind",
    desc: "Universal LLM token optimization · provider-agnostic context pruning, caching & routing",
  },
  "/governance": {
    title: "FinOps Governance",
    desc: "Tagging coverage, auto-tagging policies, untagged-cost detection across every connected cloud",
  },
  "/opportunities": {
    title: "Recommendations",
    desc: "All active cost-saving findings from the latest scan",
  },
  "/wastegraph": {
    title: "ASTRA WasteGraph",
    desc: "5-layer deep waste taxonomy · ownership inference · commitment intelligence",
  },
  "/savings-ledger": {
    title: "Savings Ledger",
    desc: "Projected vs realized savings, closed-loop verification & ASTRA Co-Worker automations",
  },
  "/partnerops": {
    title: "PartnerOps (MSP)",
    desc: "Billing-grade rebilling, invoice automation, custom reporting & margin analytics",
  },
  "/reports": {
    title: "Report Center",
    desc: "MSP-grade prebuilt & custom reports, plus multi-cloud chargeback automation",
  },
  "/remediations": {
    title: "Remediations",
    desc: "Human-in-the-loop approval queue for safe cloud cleanup actions",
  },
  "/llm-config": {
    title: "LLM Configuration",
    desc: "Bring your own keys — Anthropic, OpenAI and Google Gemini",
  },
  "/settings": {
    title: "Settings",
    desc: "Agent configuration, integrations and monitored services",
  },
};

function Shell() {
  const { pathname } = useLocation();
  const meta = TITLES[pathname] || TITLES["/executive"];
  return (
    <div className="cs-app flex">
      <Sidebar />
      <main className="flex-1 min-w-0 flex flex-col">
        <Header
          pageTitle={meta.title}
          pageDescription={meta.desc}
        />
        <div className="flex-1 bg-[hsl(var(--cs-tint))]">
          <Routes>
            <Route path="/" element={<Navigate to="/executive" replace />} />
            <Route path="/dex" element={<AskDex />} />
            <Route path="/executive" element={<ExecOverview />} />
            <Route path="/anomalies" element={<Anomalies />} />
            <Route path="/allocation" element={<Navigate to="/cloud-economics?tab=allocation" replace />} />
            <Route path="/cloud-economics" element={<CloudEconomics />} />
            <Route path="/containers" element={<ContainerOptimization />} />
            <Route path="/chargeback" element={<Navigate to="/reports?tab=chargeback" replace />} />
            <Route path="/unit-economics" element={<UnitEconomics />} />
            <Route path="/tokenmind" element={<TokenMind />} />
            <Route path="/governance" element={<Governance />} />
            <Route path="/opportunities" element={<Opportunities />} />
            <Route path="/wastegraph" element={<Navigate to="/cloud-economics?tab=wastegraph" replace />} />
            <Route path="/savings-ledger" element={<SavingsHub />} />
            <Route path="/coworkers" element={<Navigate to="/savings-ledger?tab=coworkers" replace />} />
            <Route path="/partnerops" element={<PartnerOps />} />
            <Route path="/reports" element={<ReportCenter />} />
            <Route path="/remediations" element={<Remediations />} />
            <Route path="/llm-config" element={<LLMConfig />} />
            <Route path="/settings" element={<Settings />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--cs-tint))]">
        <div className="text-sm text-muted-foreground" data-testid="auth-checking">Checking session…</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/portal/login" element={<PortalLogin />} />
          <Route path="/portal" element={<Portal />} />
          <Route path="/portal/invoice/:invoiceId" element={<PortalInvoicePrint />} />
          <Route
            path="/invoice-print/:invoiceId"
            element={
              <Protected>
                <InvoicePrint />
              </Protected>
            }
          />
          <Route
            path="*"
            element={
              <Protected>
                <Shell />
              </Protected>
            }
          />
        </Routes>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: "white",
              color: "#09090B",
              border: "1px solid #E4E4E7",
              borderRadius: "4px",
              fontFamily: "IBM Plex Sans, sans-serif",
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}
